import { cellAtWorld, heroWalkable } from '../grid.ts';
import { idiv, imax, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, GondolaRules, GondolaState, Hero, SendaNpc } from '../types.ts';
import { ev } from './combat.ts';
import { gargFlock, gargOn, gargTick } from './garg.ts';
import { shootTick } from './workshop.ts';

/**
 * Over the abyss (docs/sendas/artifice.md, chapter 2, act 2, «Sobre el abismo»; the ground in battle/cable-layout.ts).
 * The car crosses back hanging from the cable with Tormo's arms in it: the field is its open deck (the cabin on its east
 * end, Dunna shooting from its window). It goes `len` at full speed; what flies perches on the cabin's roof (and pecks
 * the hero under it) and on the cable fore and aft (battle/garg.ts): with `stop` on the cable the car stops, and while
 * it hangs there another flock comes every `stopFlock`. The deck tilts (the El Cuerno 2 ground that leans): a gust or a
 * pylon (each pylon on the way tips it as the car rides over its saddle), warned 3 s before (the cable swings, the car
 * creaks), then for `dur` what is loose slides down the deck: the hero, unless it holds on to a rail's post as it tips
 * (within `hold`; a post slid past is not caught) or is on the high side. Past the deck's edge it is over it: lost. Won at the far end.
 *
 * The deck stands still in the sim (the world goes by in the drawing: engine/cable.ts); the hero slides by its own
 * coordinates, its way cleared.
 *
 * Cost: the flyers'; one hero looked at against a handful of posts during a tilt.
 */

const fresh = (r: GondolaRules): GondolaState => ({ prog: 0, stopped: false, still: 0, flock: r.flock.first, warn: -1, tip: -1, side: 1, right: -1, next: r.tilt.first, pylon: 0, grip: false, fell: false, shot: 0, end: -1 });

/** Whether the hero holds on: by a post of the rails. */
export const gondolaHolds = (r: GondolaRules, h: { x: number; y: number }): boolean => r.holds.some((p) => withinRange(p.x, p.y, h.x, h.y, r.hold));

/** The hero slid down the deck a tick; over the edge, or onto what isn't deck: over it. */
const slide = (s: BattleState, r: GondolaRules, w: GondolaState, h: Hero): void => {
  if (w.grip) return;
  h.y += w.side * r.tilt.slide;
  h.destX = h.x;
  h.destY = h.y;
  h.ax = h.x;
  h.ay = h.y;
  h.path = [];
  h.moving = false;
  // Past the edge's line, or onto what isn't deck (the rear's corners, by the cabin): over it.
  if (Math.abs(h.y) > r.fall || !heroWalkable(s, cellAtWorld(s, h.x, h.y))) {
    w.fell = true;
    ev(s, { t: 'senda', kind: 'fell', npc: '', x: h.x, y: h.y });
  }
};

export const gondolaTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.gondola;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.gondola ??= fresh(r));
  if (!s.started) return;
  gargTick(s);
  const d = st.npcs.find((n): n is SendaNpc => n.id === 'dunna' && n.hp > 0);
  if (d) {
    if (d.flinch > 0) d.flinch--;
    w.shot = shootTick(s, d, r.dunna, w.shot);
  }
  if (w.end >= 0 || w.fell) return;
  // Flocks off the abyss: for the cable first, the roof, else at the hero.
  if (s.waveT >= w.flock) {
    w.flock = s.waveT + r.flock.every;
    gargFlock(s, r.flock.n, r.flock.crows, [2, 1], 10000);
    cue('flock');
  }
  // Enough on the cable: the car stops; hanging there, another flock now and then.
  const stopped = gargOn(s, 2) >= r.stop;
  if (stopped !== w.stopped) {
    w.stopped = stopped;
    w.still = 0;
    ev(s, { t: 'senda', kind: 'carStop', npc: '', x: 0, y: 0, n: stopped ? 1 : 0 });
    cue(stopped ? 'stop' : 'go');
  }
  if (stopped) {
    if (++w.still >= r.stopFlock) {
      w.still = 0;
      gargFlock(s, r.flock.n, 0, [2, 1], 10000);
      cue('stopFlock');
    }
  } else {
    w.prog++;
    // Along the way: the lines' moments (a tenth at a time), the half.
    if (w.prog % imax(1, idiv(r.len, 20)) === 0) cue(`way${idiv(w.prog * 20, r.len)}`);
    if (w.prog >= r.len) {
      w.end = s.tick;
      ev(s, { t: 'senda', kind: 'pylon', npc: '', x: 0, y: 0, n: 9 });
      cue('arrive');
      return;
    }
  }
  // The tilts: a gust now and then, each pylon's saddle as the car rides over it.
  const pylon = w.pylon < r.pylons.length && w.prog + r.tilt.warn >= (r.pylons[w.pylon] as number);
  if (w.warn < 0 && (s.tick >= w.next || pylon)) {
    w.warn = s.tick;
    w.tip = s.tick + r.tilt.warn;
    w.right = w.tip + r.tilt.dur;
    w.side = pylon ? (w.pylon % 2 === 0 ? 1 : -1) : rngInt(s.rng, 2) === 0 ? 1 : -1;
    if (pylon) {
      ev(s, { t: 'senda', kind: 'pylon', npc: '', x: 0, y: 0, n: w.pylon });
      w.pylon++;
      cue(`pylon${w.pylon}`);
    }
    ev(s, { t: 'senda', kind: 'tiltWarn', npc: '', x: 0, y: 0, n: w.side });
    cue('tilt');
  }
  if (w.warn >= 0) {
    if (s.tick === w.tip) {
      ev(s, { t: 'senda', kind: 'tilt', npc: '', x: 0, y: 0, n: w.side });
      const h = s.heroes.find((x) => x.downTicks === 0);
      w.grip = !!h && gondolaHolds(r, h);
    }
    if (s.tick >= w.tip && s.tick < w.right) {
      for (const h of s.heroes) if (h.downTicks === 0) slide(s, r, w, h);
    } else if (s.tick >= w.right) {
      w.warn = -1;
      w.next = s.tick + rngRange(s.rng, r.tilt.every[0], r.tilt.every[1]);
      cue('righted');
    }
  }
};

/** The way across. */
export const gondolaGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.gondola;
  const w = s.senda?.gondola;
  if (!r || !w) return { done: 0, need: 1 };
  return { done: w.prog, need: r.len };
};

/** Won at the far end (a moment after); lost over the edge. */
export const gondolaResult = (s: BattleState): 'won' | 'lost' | null => {
  const w = s.senda?.gondola;
  if (!w) return null;
  if (w.fell) return 'lost';
  return w.end >= 0 && s.tick >= w.end + 12 ? 'won' : null;
};
